import type { ReactNode } from 'react';
import { PolicyLinks } from './Policy';

const SUPPORT = 'support@buddybay.net';

/** Top-of-page links on the signed-out front page. */
export function LandingNav() {
  return (
    <nav className="landing-nav" aria-label="Front page">
      <img src="/brand/buddybay-icon.svg" alt="" width={36} height={36} />
      <span className="spacer" />
      <a href="#about">About</a>
      <a href="#parents">Parents</a>
      <a href="#faq">FAQ</a>
      <a href="#contact">Contact</a>
    </nav>
  );
}

const FEATURES: { icon: string; title: string; text: string }[] = [
  { icon: '🦊', title: 'Make your buddy', text: 'Be a Little Creature (a fox, otter or axolotl) or a Humanoid, then dress them up. Each account can have up to three characters.' },
  { icon: '🏖️', title: 'Explore the bay', text: 'Wander Town Square and Pebble Beach with players from all over, and visit your friends in their homes.' },
  { icon: '🎣', title: 'Play games', text: 'Fish off the pier, race in the Driftwood Derby, and play Tide Trivia, Shell Shuffle, Pop-a-Buoy and Bubble Squad at the Bayside Arcade.' },
  { icon: '🏡', title: 'Decorate your home', text: 'Earn coins, shop for clothes and furniture, decorate your cottage and grow crops on the farm out back.' },
  { icon: '🤝', title: 'Make friends', text: 'Chat, add friends, send each other gifts and team up in games.' },
  { icon: '🎃', title: 'Seasonal events', text: 'Festivals like Haunted Harbor bring limited-time areas, games and collectibles, plus achievements, collections and daily challenges all year.' },
];

const FAQ: { q: string; a: ReactNode }[] = [
  { q: 'Is Buddy Bay free?', a: 'Yes. Buddy Bay is free to play. Coins and items are earned in the game, and there is nothing to buy with real money.' },
  { q: 'What do I need to play?', a: 'A computer or tablet with an up-to-date web browser (Chrome, Edge, Firefox or Safari). There is nothing to download or install.' },
  { q: 'Who can play?', a: 'Players aged 8 and older in the United States and Canada. Players 16 and under need a parent or guardian to approve their account.' },
  { q: "Why can't I create an account?", a: 'Buddy Bay is in a closed beta, so new accounts may need an invite code, or may be paused for a while. Check back soon, or email us.' },
  { q: 'How does parent approval work?', a: <>When a player 16 or under signs up, we email their parent. The parent signs in to (or creates) their own account, approves, and can set up parent controls at <a href="/family">buddybay.net/family</a>. Read the <a href="/policies/parents">Parent Guide</a> to learn more.</> },
  { q: 'I forgot my password. What do I do?', a: <>Choose <b>Forgot your password?</b> under the sign-in form. Players 16 and under can ask a parent to reset it from parent controls.</> },
  { q: 'How do I report a player or a problem?', a: <>In the game, click on any player and choose <b>Report</b>. Found a bug? Use the 🐞 button. You can also email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</> },
];

/** Everything below the sign-in form on the signed-out front page. */
export function LandingInfo() {
  return (
    <div className="landing">
      <section id="about" className="landing-section">
        <div className="landing-head">
          <p className="eyebrow">What is Buddy Bay?</p>
          <h2>A friendly online world to explore together</h2>
          <p className="lede">Buddy Bay is a cartoon town by the sea that you play in your web browser. Make a character, explore the town and beach, play mini-games to earn coins, decorate your own home and make friends, all in a place built for kids and safe for everyone.</p>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <article key={f.title} className="feature">
              <span className="feature-icon" aria-hidden="true">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="parents" className="landing-section split">
        <div className="landing-head">
          <p className="eyebrow">For parents</p>
          <h2>Safe by design, with you in charge</h2>
          <p className="lede">Buddy Bay is built for players 8 and up. Every account for a player 16 and under is linked to a parent's account, and parents decide how their child plays.</p>
          <div className="row-wrap">
            <a className="btn" href="/policies/parents">Read the Parent Guide</a>
            <a className="btn ghost" href="/policies/children">Children's Privacy Notice</a>
          </div>
        </div>
        <ul className="check-list">
          <li><b>Parent approval.</b> Players 16 and under can't play until a parent approves the account.</li>
          <li><b>Chat by age.</b> Players 8–13 can only use friendly preset phrases. For 14–16, parents choose.</li>
          <li><b>Filtered chat.</b> Emails, phone numbers, links and bad language are blocked for everyone.</li>
          <li><b>Real moderators.</b> Our team reviews every report, and rule-breakers get warnings or bans.</li>
          <li><b>Parent controls.</b> Approve friend requests, set daily time limits and play hours, turn games and gifts off, and read your child's chat.</li>
          <li><b>No ads, ever.</b> We don't show ads or sell personal information, and there are no real-money purchases.</li>
        </ul>
      </section>

      <section id="faq" className="landing-section">
        <div className="landing-head">
          <p className="eyebrow">FAQ</p>
          <h2>Questions and answers</h2>
        </div>
        <div className="faq">
          {FAQ.map((f) => (
            <details key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section id="contact" className="landing-section">
        <div className="contact-card">
          <div className="landing-head">
            <p className="eyebrow">Contact us</p>
            <h2>We're here to help</h2>
            <p className="lede">Questions, account help, privacy requests or a safety concern? Email us and a real person will reply.</p>
          </div>
          <a className="btn contact-email" href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
        </div>
      </section>

      <footer className="landing-footer">
        <img src="/brand/buddybay-logo.svg" alt="Buddy Bay" height={40} />
        <PolicyLinks />
        <p className="muted small-print">
          Buddy Bay is in closed beta. · <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> · © {new Date().getFullYear()} Buddy Bay
        </p>
      </footer>
    </div>
  );
}
